import { Attributes, DateUtils, List, Misc, Rules, Validation } from '@singularsystems/neo-core';
import BankDetail from '../ValueObjects/BankDetail';
import Note from '../Notes/Note';
import INoteListHost from '../Notes/INoteListHost';
import TemporalEntity from '../TemporalEntity';
import AuthorisationValues from '../ValueObjects/AuthorisationValues';
import { IPreAuthorisable } from '../IPreAuthorisable';
import { IAuthorisable } from '../IAuthorisable';
import { AppService, DomainTypes } from '../../DomainTypes';
import IssuerFieldConfig from '../Config/FieldConfig/IssuerFieldConfig';
import { MandatoryAndVisibleSetting } from '../Config/FieldConfig/FieldSetting.enum';
import { isMandatory } from '../Config/FieldConfig/FieldSetting.enum';
import IssuerContact from '../Contacts/IssuerContact';

export default class Issuer extends TemporalEntity implements INoteListHost, IAuthorisable, IPreAuthorisable {

    static typeName = "Issuer";

    constructor() {
        super();
        this.makeObservable();
    }

    public issuerId: number = 0;

    @Rules.Required()
    public issuerName: string = "";

    @Rules.Required()
    public issuerTypeId: number | null = null;

    @Rules.Required()
    public legalEntityTypeId: number | null = null;

    @Attributes.Nullable()
    public internalRatingId: number | null = null;

    @Attributes.Nullable()
    public intraGroupId: number | null = null;

    public sectorId: number | null = null;

    @Attributes.Nullable()
    public assetClassId: number | null = null;

    @Attributes.Nullable()
    public countryId: number | null = null;

    public bankDetails: BankDetail = new BankDetail();

    public registrationNumber: string = "";

    public cisNumbers: string = "";

    public taxRefNo: string = "";

    public postalAddress: string = "";

    public physicalAddress: string = "";

    public withholdingAgentReferenceNumber: string = "";

    public ledgerAccount: string = "";

    @Attributes.Date()
    public dateOfRegistration: Date | null = null;

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public ficaDate: Date | null = null;

    public isRestricted: boolean = false;

    @Attributes.Nullable()
    public authorisedPersonContactId: number | null = null;

    @Attributes.NoTracking(Misc.SerialiseType.FullOnly)
    public authorisedContactName: string | null = null;

    @Attributes.ChildList(IssuerContact)
    public contacts = new List(IssuerContact);

    public sanctionScreeningPerformed: boolean = false;

    public notes = new List(Note);

    public preAuthorisation : AuthorisationValues = new AuthorisationValues();

    public authorisation: AuthorisationValues = new AuthorisationValues();

    public isPendingDelete: boolean = false;

    @Attributes.Display("SARS Nature Of Person")
    @Attributes.Nullable()
    public sarsNatureOfPersonId: number | null = null;

    @Attributes.Float()
    public lossGivenDefault: number = 0;

    @Attributes.Nullable()
    public referralContactId: number | null = null;

    @Attributes.NoTracking(Misc.SerialiseType.FullOnly)
    public referralContactName: string | null = null;
    // Client only properties / methods

    public setIdOnNote(note: Note): void {
        note.issuerId = this.issuerId;
    }

    protected addBusinessRules(rules: Validation.Rules<this>) {
        super.addBusinessRules(rules);

        const isDigit = (testString: string): boolean => !/[^0-9]/.test(testString);

        rules.failWhen(c => !isDigit(c.taxRefNo), "Tax reference number can only contain numbers");
        rules.failWhen(c => !isDigit(c.cisNumbers), "CIS number can only contain numbers");

        // Rules affected by configurable mandatory settings
        rules.failWhen(c => !c.registrationNumber && this.isRequired("registrationNumber"), "Registration number is required.");
        rules.failWhen(c => !c.cisNumbers && this.isRequired("cisNumbers"), "CIS numbers is required.");
        rules.failWhen(c => !c.taxRefNo && this.isRequired("taxRefNo"), "Tax ref no. is required.");
        rules.failWhen(c => !c.postalAddress && this.isRequired("postalAddress"), "Postal address is required.");
        rules.failWhen(c => !c.physicalAddress && this.isRequired("physicalAddress"), "Physical address is required.");
        rules.failWhen(c => !c.internalRatingId && this.isRequired("internalRatingId"), "Internal rating is required.");
        rules.failWhen(c => !c.intraGroupId && this.isRequired("intraGroupId"), "Intra group is required.");
        rules.failWhen(c => !c.sectorId && this.isRequired("sectorId"), "Sector is required.");
        rules.failWhen(c => !c.assetClassId && this.isRequired("assetClassId"), "Asset class is required.");
        rules.failWhen(c => !c.countryId && this.isRequired("countryId"), "Country is required.");
        rules.failWhen(c => !c.withholdingAgentReferenceNumber && this.isRequired("withholdingAgentReferenceNumber"), "Withholding agent reference number is required.");
        rules.failWhen(c => !c.ledgerAccount && this.isRequired("ledgerAccount"), "Ledger account is required.");
        rules.failWhen(c => !c.ficaDate && this.isRequired("ficaDate"), "Fica date is required.");
        rules.failWhen(c => !c.authorisedPersonContactId && this.isRequired("authorisedPersonContactId"), "Authorised contact is required.");
        rules.failWhen(c => c.contacts.length === 0 && this.isRequired("contacts"), "At least 1 linked contact is required.");
        rules.failWhen(c => c.lossGivenDefault !== null && !(c.lossGivenDefault >= 0 && c.lossGivenDefault <= 1), "Loss Given Default must be between 0% and 100%");
    }

    public toString(): string {
        if (this.isNew || !this.issuerName) {
            return "New issuer";
        } else {
            return this.issuerName;
        }
    }

    private isRequired(fieldName: keyof Issuer & keyof IssuerFieldConfig) {
        return isMandatory(AppService.get(DomainTypes.Services.DataCache).fieldConfig.issuerFields[fieldName] as MandatoryAndVisibleSetting);
    }
}